import { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, Shield, Crown, Users, Eye } from 'lucide-react';
import { membersApi } from '../../../api/client';
import type { OrgMember, OrgRole } from '../../../api/client';
import { RoleBadge } from '../../../components/team/RoleBadge';
import { usePermissions } from '../../../hooks/usePermissions';
import { ROLE_PERMISSIONS } from '../../../lib/permissions';
import type { Permission } from '../../../lib/permissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

const ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  owner:  'Full control over the organization, including ownership transfer and deletion.',
  admin:  'Can manage members, invitations, bets, bankroll, and organization settings.',
  member: 'Can place and manage bets. Cannot change membership or settings.',
  viewer: 'Read-only access to the organization. No write permissions.',
};

const ROLE_ICONS: Record<OrgRole, React.ElementType> = {
  owner:  Crown,
  admin:  Shield,
  member: Users,
  viewer: Eye,
};

const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  {
    label: 'Organization',
    permissions: [
      'organization.view',
      'organization.update',
      'organization.delete',
      'organization.transfer_ownership',
    ],
  },
  {
    label: 'Members',
    permissions: [
      'members.view',
      'members.invite',
      'members.remove',
      'members.update_role',
    ],
  },
  {
    label: 'Betting',
    permissions: [
      'bets.view',
      'bets.create',
      'bets.update',
      'bets.settle',
      'bets.delete',
    ],
  },
  {
    label: 'Bankroll',
    permissions: [
      'bankroll.view',
      'bankroll.update_settings',
      'bankroll.reset',
    ],
  },
  {
    label: 'AI & Recommendations',
    permissions: [
      'recommendations.view',
      'recommendations.release',
      'recommendations.approve',
      'calibration.view',
      'calibration.run',
      'calibration.promote',
    ],
  },
  {
    label: 'Audit & Admin',
    permissions: [
      'audit_log.view',
      'audit_log.view_all',
      'settings.view',
      'settings.update',
    ],
  },
  {
    label: 'Billing',
    permissions: [
      'billing.view',
      'billing.manage',
    ],
  },
];

function permissionLabel(p: Permission): string {
  return p
    .replace('.', ' → ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Role Card ──────────────────────────────────────────────────────────────────

function RoleCard({
  role,
  memberCount,
}: {
  role: OrgRole;
  memberCount: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const perms = ROLE_PERMISSIONS[role] as readonly Permission[];
  const Icon = ROLE_ICONS[role];

  return (
    <div className="op-panel p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 p-2 rounded-lg bg-surface-3 border border-surface-4 flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <RoleBadge role={role} size="md" />
              <span className="text-xs text-gray-600">
                {memberCount} {memberCount === 1 ? 'member' : 'members'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1.5 max-w-lg">{ROLE_DESCRIPTIONS[role]}</p>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-brand hover:text-brand/80 flex-shrink-0 transition-colors"
        >
          {expanded ? 'Hide permissions' : 'View permissions'}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 pt-2 border-t border-surface-4">
          {PERMISSION_GROUPS.map(({ label, permissions }) => {
            const relevant = permissions.filter((p) => perms.includes(p));
            if (relevant.length === 0) return null;
            return (
              <div key={label}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {relevant.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center text-[10px] font-medium text-win bg-win/10 border border-win/20 rounded px-1.5 py-0.5"
                    >
                      {permissionLabel(p)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const ROLES: OrgRole[] = ['owner', 'admin', 'member', 'viewer'];

export function AdminRolesPage() {
  const { can } = usePermissions();
  const canView = can('organization.view');

  const [members,   setMembers]   = useState<OrgMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    membersApi
      .listMembers()
      .then(setMembers)
      .catch((err) => setError((err as Error).message ?? 'Failed to load members'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  const countsByRole = (role: OrgRole) =>
    members.filter((m) => m.role === role).length;

  if (!canView) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view roles.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Roles &amp; Permissions</h2>
        <p className="text-sm text-gray-500 mt-1">
          Review organization roles, their member counts, and granted permissions.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading roles…
        </div>
      ) : (
        <div className="space-y-4">
          {ROLES.map((role) => (
            <RoleCard
              key={role}
              role={role}
              memberCount={countsByRole(role)}
            />
          ))}
        </div>
      )}

      <div className="op-panel p-4 bg-surface-2/50">
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-400">Role restrictions: </span>
          Users cannot self-promote. Admins cannot create or assign owners. Only the owner can
          transfer ownership or remove another owner.
        </p>
      </div>
    </div>
  );
}
